import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PipeTrackCell, PipeTrackComponent } from '../pipe-track/pipe-track.component';
import { buildPipeTrackCells, buildPipeTrackSummary } from '../pipe-track/pipe-track-cells';
import { LivePipelineEntry, pipelineCounts, runningWork, segmentKindOf } from '../../models/live-pipeline.model';
import { elapsedClock } from '../../shared/elapsed-clock';

/** Eine der drei Zahlen rechts im Band. */
interface LiveRunStat {
  testId: string;
  labelKey: string;
  value: number;
  /** Farbgebung der Zahl; {@code null} laesst sie blass. */
  tone: 'passed' | 'failed' | 'open' | null;
}

/**
 * Das Live-Band unter der Zeile eines laufenden Pipeline-Durchlaufs (NEX-1233):
 * laufender Test, Punkt-Leiste, Zahlen und Laufzeit.
 *
 * <p>Ob ein Lauf ein Band bekommt, entscheidet die Uebersicht — das Band zeigt den
 * Durchlauf, den sie ihm gibt. Ein zweiter Zustandstest hier wuerde dieselbe Regel
 * ein zweites Mal beschreiben und koennte davon abweichen.
 *
 * <p>Die Leiste ist dieselbe wie auf der Dashboard-Karte und bekommt ihre Felder
 * aus derselben Ableitung; eine zweite Rechnung daneben zeigte sonst einen anderen
 * Stand desselben Durchlaufs.
 */
@Component({
  selector: 'app-live-run-inline',
  imports: [TranslatePipe, PipeTrackComponent],
  templateUrl: './live-run-inline.component.html',
  styleUrl: './live-run-inline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveRunInlineComponent {
  private readonly translate = inject(TranslateService);

  readonly pipeline = input.required<LivePipelineEntry>();

  protected readonly counts = computed(() => pipelineCounts(this.pipeline()));

  protected readonly current = computed(() => runningWork(this.pipeline()));

  protected readonly segmentKind = computed(() => segmentKindOf(this.pipeline()));

  protected readonly currentLabelKey = computed(() =>
    this.segmentKind() === 'JOBS' ? 'liveRunInline.currentJob' : 'liveRunInline.currentTest',
  );

  /**
   * Bestanden, fehlgeschlagen und offen — in dieser Reihenfolge, wie im Design. Die
   * Null bei den Fehlschlaegen bleibt blass: erst ein echter Fehler traegt die rote
   * Farbe, sonst liest sich jede Zeile als auffaellig.
   */
  protected readonly stats = computed<LiveRunStat[]>(() => {
    const { total, done, passed, failed } = this.counts();
    return [
      { testId: 'live-run-passed', labelKey: 'liveRunInline.passed', value: passed, tone: 'passed' },
      {
        testId: 'live-run-failed',
        labelKey: 'liveRunInline.failed',
        value: failed,
        tone: failed > 0 ? 'failed' : null,
      },
      { testId: 'live-run-open', labelKey: 'liveRunInline.open', value: Math.max(0, total - done), tone: 'open' },
    ];
  });

  protected readonly cells = computed<PipeTrackCell[]>(() => buildPipeTrackCells(this.pipeline(), this.translate));

  protected readonly trackLabel = computed(() =>
    buildPipeTrackSummary(this.cells(), this.segmentKind(), this.translate),
  );

  protected readonly elapsed = elapsedClock(() => this.pipeline().startedAt ?? null);
}
